import { useCallback, useRef, useState } from 'react';
import {
	type OperationVariables,
	type MutationResult,
	type MutationFunctionOptions,
} from '@apollo/react-common';
import {
	type MutationHookOptions,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- `@confluence/apollo-relay-interop` package is the only one where such imports are allowed
	useMutation,
} from '@apollo/react-hooks';
import { type GraphQLTaggedNode, commitMutation, useRelayEnvironment } from 'react-relay';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import { isRelayEnabled } from './isRelayEnabled.ts';
import { useMemoObj } from './useMemoObj.ts';
import { toDocumentNode } from './toDocumentNode.ts';
import { useMutationCacheBridgeSync } from './cacheBridge/useMutationCacheBridgeSync.ts';
import {
	syncApolloRefetchQueriesFromCache,
	syncRelayRefetchQueryPromise,
} from './cacheBridge/mutation/syncRefetchQueryResult.ts';
import {
	applyOptimisticMutationUpdate,
	type OptimisticMutationUpdateState,
} from './optimisticInterop/optimisticMutationUpdate.ts';
import { usePendingOptimisticInteropUpdates } from './optimisticInterop/usePendingOptimisticInteropUpdates.ts';
// Package-local wrapper (not `react-relay`'s) — surfaces refetch errors as `ApolloErrorInterop`.
import { fetchQuery } from './fetchQuery.ts';

export type MutationFunctionInteropSupportedOptions<TData, TVariables> = Pick<
	MutationFunctionOptions<TData, TVariables>,
	'variables' | 'optimisticResponse' | 'update'
> & {
	// @deprecated - use mutation options instead
	refetchQueries?: never;
};

export type MutationFunctionInterop<TData, TVariables> = (
	options?: MutationFunctionInteropSupportedOptions<TData, TVariables>,
) => Promise<{ data?: TData }>;

export type UseMutationInteropSupportedResult<TData, TVariables> = [
	MutationFunctionInterop<TData, TVariables>,
	Pick<MutationResult<TData>, 'data' | 'error' | 'loading' | 'called'>,
];

/** Helper type for easier overrides on the `UseMutationInteropSupportedArgs` type */
export type MutationUpdateParamType<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = Required<MutationHookOptions<TData, TVariables>>['update'];

type MutationInteropRefetchQueryEntry = {
	query: GraphQLTaggedNode;
	variables?: Record<string, unknown>;
};

type MutationInteropRefetchQueries<TData> =
	| Array<MutationInteropRefetchQueryEntry>
	| ((response: { data: TData }) => Array<MutationInteropRefetchQueryEntry>);

export type UseMutationInteropSupportedArgs<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> =
	| [GraphQLTaggedNode]
	| [
			GraphQLTaggedNode,
			Pick<
				MutationHookOptions<TData, TVariables>,
				| 'variables'
				| 'onCompleted'
				| 'onError'
				| 'errorPolicy'
				| 'optimisticResponse'
				| 'context'
				| 'fetchPolicy'
				| 'awaitRefetchQueries'
			> & {
				refetchQueries?: MutationInteropRefetchQueries<TData>;
				/**
				 * Called after a mutation completes. The first parameter (Apollo cache) is
				 * deprecated and will be removed — do not use it. Use `getAGGClient()` directly
				 * if cache writes are needed.
				 *
				 * @param _ - **@deprecated** The Apollo cache/client parameter. Typed as `never`
				 *   to prevent access. Will be removed in a future version.
				 * @param mutationResult - The result of the mutation.
				 */
				update?(
					/** @deprecated Do not use — the Apollo cache parameter is not supported. Use interops directly for cache writes. */
					_: never,
					mutationResult: Parameters<MutationUpdateParamType<TData, TVariables>>[1],
				): ReturnType<MutationUpdateParamType<TData, TVariables>>;
				/**
				 * Ensures that the interop in Apollo build variant uses the AGG client
				 */
				interopApolloAggClient?: boolean;
				/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
				__callerPackage__?: string;
			},
	  ];

export function useMutationInterop<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	...args: UseMutationInteropSupportedArgs<TData, TVariables>
): UseMutationInteropSupportedResult<TData, TVariables> {
	if (isRelayEnabled(args[1]?.__callerPackage__, { phase: 'mutation' })) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		return useMutationInteropRelay<TData, TVariables>(...args);
	} else {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		return useMutationInteropApollo<TData, TVariables>(...args);
	}
}

function useMutationInteropApollo<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	...[mutation, options]: UseMutationInteropSupportedArgs<TData, TVariables>
): UseMutationInteropSupportedResult<TData, TVariables> {
	const { interopApolloAggClient, refetchQueries, ...apolloOptions } = options ?? {};
	const refetchQueriesRef = useRef(refetchQueries);
	refetchQueriesRef.current = refetchQueries;

	const [mutate, result] = useMutation<TData, TVariables>(
		...[
			toDocumentNode(mutation),
			{
				...apolloOptions,
				...(interopApolloAggClient
					? {
							client:
								window['GLOBAL_APOLLO_AGG_CLIENT'] ?? (window as any)['MOCK_GATEWAY_APOLLO_CLIENT'],
						}
					: {}),
				update(_: any, mutationResult: Parameters<MutationUpdateParamType<TData, TVariables>>[1]) {
					return apolloOptions?.update?.(null as never, mutationResult);
				},
			},
		],
	);
	const mutateWithRefetchSync = useCallback(
		(mutationOptions?: MutationFunctionInteropSupportedOptions<TData, TVariables>) => {
			let refetchEntriesForSync: readonly unknown[] | undefined;
			const latestRefetchQueries = refetchQueriesRef.current;
			const apolloMutationOptions =
				typeof latestRefetchQueries === 'undefined'
					? mutationOptions
					: ({
							...mutationOptions,
							// Capture refetch entries per mutate call. A shared hook-level ref can be
							// overwritten by overlapping mutations before the first call mirrors its result.
							refetchQueries: createRefetchQueriesForMutation(latestRefetchQueries, (entries) => {
								refetchEntriesForSync = entries;
							}),
						} as MutationFunctionOptions<TData, TVariables>);

			return mutate(apolloMutationOptions).then((mutationResult) => {
				syncApolloRefetchQueriesFromCache({
					entries: refetchEntriesForSync,
					awaitRefetchQueries: apolloOptions.awaitRefetchQueries,
					useAggClient: interopApolloAggClient,
				});

				return mutationResult;
			});
		},
		[apolloOptions.awaitRefetchQueries, interopApolloAggClient, mutate],
	);
	const mutateWithCacheBridge = useMutationCacheBridgeSync<TData, TVariables>({
		source: 'apollo',
		mutation,
		mutate: mutateWithRefetchSync,
		defaultVariables: apolloOptions.variables,
		defaultOptimisticResponse:
			typeof apolloOptions.optimisticResponse === 'function'
				? undefined
				: apolloOptions.optimisticResponse,
		fetchPolicy: apolloOptions.fetchPolicy,
		useAggClient: interopApolloAggClient,
		callerPackage: options?.__callerPackage__,
	});

	return [mutateWithCacheBridge, result];
}

function createRefetchQueriesForMutation<TData, TVariables extends OperationVariables>(
	refetchQueries: MutationInteropRefetchQueries<TData> | undefined,
	captureEntries: (entries: Array<MutationInteropRefetchQueryEntry>) => void,
): MutationFunctionOptions<TData, TVariables>['refetchQueries'] | undefined {
	if (typeof refetchQueries === 'undefined') return undefined;

	const convertEntries = (graphqlTaggedNodeArray: Array<MutationInteropRefetchQueryEntry>) => {
		captureEntries(graphqlTaggedNodeArray);

		return graphqlTaggedNodeArray.map((value) => {
			return {
				query: toDocumentNode(value.query),
				variables: value.variables,
			};
		});
	};

	if (typeof refetchQueries !== 'function') {
		return convertEntries(refetchQueries ?? []);
	}

	return (response: { data: TData }) => {
		return convertEntries(refetchQueries(response));
	};
}

function useMutationInteropRelay<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	...[mutation, options]: UseMutationInteropSupportedArgs<TData, TVariables>
): UseMutationInteropSupportedResult<TData, TVariables> {
	const environment = useRelayEnvironment();
	// Memoize options whose identity must be stable across renders.
	const variables = useMemoObj<TVariables>(options?.variables, options?.__callerPackage__);
	const context = useMemoObj(options?.context, options?.__callerPackage__);
	const variablesRef = useRef(variables);
	variablesRef.current = variables;
	const contextRef = useRef(context);
	contextRef.current = context;

	const [data, setData] = useState<TData | undefined>(undefined);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<any>(undefined);
	const [called, setCalled] = useState(false);

	const onCompletedRef = useRef(options?.onCompleted);
	onCompletedRef.current = options?.onCompleted;

	const onErrorRef = useRef(options?.onError);
	onErrorRef.current = options?.onError;

	const defaultUpdateRef = useRef(options?.update);
	defaultUpdateRef.current = options?.update;

	const defaultOptimisticResponseRef = useRef(options?.optimisticResponse);
	defaultOptimisticResponseRef.current = options?.optimisticResponse;

	const refetchQueriesRef = useRef(options?.refetchQueries);
	refetchQueriesRef.current = options?.refetchQueries;

	const awaitRefetchQueriesRef = useRef(options?.awaitRefetchQueries);
	awaitRefetchQueriesRef.current = options?.awaitRefetchQueries;

	const useAggClientRef = useRef(options?.interopApolloAggClient);
	useAggClientRef.current = options?.interopApolloAggClient;

	const { trackOptimisticUpdate, untrackOptimisticUpdate } =
		usePendingOptimisticInteropUpdates<TData>();

	const mutate = useCallback(
		(mutationOptions?: MutationFunctionInteropSupportedOptions<TData, TVariables>) => {
			setCalled(true);
			setLoading(true);
			setError(undefined);

			// Consumer callbacks must never break the mutation pipeline.
			const safeOnError = (err: unknown) => {
				try {
					onErrorRef.current?.(err as ApolloErrorInterop);
				} catch {
					// ignore
				}
			};

			const operationVariables = mutationOptions?.variables || variablesRef.current || {};

			// Call-time options take priority over hook-level defaults.
			const update = mutationOptions?.update ?? defaultUpdateRef.current;
			const optimisticResponse =
				mutationOptions?.optimisticResponse ?? defaultOptimisticResponseRef.current;

			// Capture at mutate-call time so an in-flight options swap doesn't change semantics.
			const refetchQueries = refetchQueriesRef.current;
			const awaitRefetchQueries = awaitRefetchQueriesRef.current;

			// Apollo parity: invoke update immediately with optimistic data.
			let optimisticUpdateState: OptimisticMutationUpdateState<TData>;
			try {
				optimisticUpdateState = applyOptimisticMutationUpdate<TData, TVariables>({
					optimisticResponse,
					operationVariables: operationVariables as TVariables,
					update,
				});
				trackOptimisticUpdate(optimisticUpdateState);
			} catch (err) {
				safeOnError(err);
				setError(err);
				setLoading(false);
				return Promise.reject(err);
			}

			return new Promise<{ data?: TData }>((resolve, reject) => {
				const cleanupTrackedOptimisticUpdate = () => {
					untrackOptimisticUpdate(optimisticUpdateState);
				};

				const rollbackOptimistic = () => {
					try {
						optimisticUpdateState.rollbackOptimisticUpdate();
					} finally {
						cleanupTrackedOptimisticUpdate();
					}
				};

				commitMutation(environment, {
					mutation,
					cacheConfig: { metadata: contextRef.current },
					variables: operationVariables,
					optimisticResponse: optimisticUpdateState.resolvedOptimisticResponse as any,
					onCompleted: (response: any, errors) => {
						try {
							if (errors && errors.length > 0) {
								const err = new Error(errors[0].message);
								rollbackOptimistic();
								safeOnError(err);
								setError(err);
								setLoading(false);
								reject(err);
								return;
							}

							// User update callbacks often read/write query interop caches. Relay does
							// not give those writes Apollo's optimistic layer, so restore captured
							// interop cache writes before applying the real response.
							try {
								optimisticUpdateState.rollbackCapturedInteropWrites(() => {
									update?.(null as never, { data: response as TData });
								});
							} finally {
								cleanupTrackedOptimisticUpdate();
							}

							let resolvedEntries: Array<MutationInteropRefetchQueryEntry> = [];
							if (refetchQueries) {
								resolvedEntries =
									typeof refetchQueries === 'function'
										? refetchQueries({ data: response as TData })
										: refetchQueries;
							}

							const refetchPromises = resolvedEntries.map((entry) => {
								const refetchVariables = entry.variables ?? {};
								return syncRelayRefetchQueryPromise(
									fetchQuery(environment, entry.query, refetchVariables, {
										fetchPolicy: 'network-only',
									}).toPromise(),
									{
										query: entry.query,
										variables: refetchVariables,
										useAggClient: useAggClientRef.current,
									},
								);
							});

							if (awaitRefetchQueries && refetchPromises.length > 0) {
								// Apollo parity: while refetches are in flight, keep `loading` true and
								// hold `data` undefined — only expose it once everything has settled.
								onCompletedRef.current?.(response);
								Promise.all(refetchPromises).then(
									() => {
										setData(response as TData);
										setLoading(false);
										resolve({ data: response as TData });
									},
									(refetchError) => {
										safeOnError(refetchError);
										setError(refetchError);
										setLoading(false);
										reject(refetchError);
									},
								);
								return;
							}

							setData(response as TData);
							setLoading(false);
							onCompletedRef.current?.(response);

							if (refetchPromises.length > 0) {
								// Fire-and-forget refetches.
								Promise.all(refetchPromises).catch((refetchError) => {
									// eslint-disable-next-line no-console
									console.warn('[Relay migration] refetchQueries error:', refetchError);
								});
							}
							resolve({ data: response as TData });
						} catch (err) {
							safeOnError(err);
							setData(undefined);
							setError(err);
							setLoading(false);
							reject(err);
						}
					},
					onError: (err: any) => {
						rollbackOptimistic();
						safeOnError(err);
						setError(err);
						setLoading(false);
						reject(err);
					},
				});
			});
		},
		[environment, mutation, trackOptimisticUpdate, untrackOptimisticUpdate],
	);
	const mutateWithCacheBridge = useMutationCacheBridgeSync<TData, TVariables>({
		source: 'relay',
		mutation,
		mutate,
		defaultVariables: variables,
		defaultOptimisticResponse:
			typeof options?.optimisticResponse === 'function' ? undefined : options?.optimisticResponse,
		fetchPolicy: options?.fetchPolicy,
		useAggClient: options?.interopApolloAggClient,
		callerPackage: options?.__callerPackage__,
	});

	return [mutateWithCacheBridge, { data, loading, error, called }];
}
